import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search, Loader2, RefreshCw, Package, BarChart3, Filter, Boxes, Wrench, ShoppingBag,
  ChevronRight, ChevronLeft, Printer, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, printTable } from '../lib/printUtils';

const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');

export default function StockBodega() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [soloStock, setSoloStock] = useState(false);
  const [categoria, setCategoria] = useState('');
  const [familiaFilter, setFamiliaFilter] = useState('');
  const [clasificaciones, setClasificaciones] = useState(null);
  const [activeTab, setActiveTab] = useState('stock');

  // Consumo
  const [consumo, setConsumo] = useState([]);
  const [consumoLoading, setConsumoLoading] = useState(false);
  const now = new Date();
  const [fechaDesde, setFechaDesde] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
  const [fechaHasta, setFechaHasta] = useState(now.toISOString().split('T')[0]);

  // Drill-down
  const [drilldown, setDrilldown] = useState(null); // { codigo, nombre }
  const [drillData, setDrillData] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillGroup, setDrillGroup] = useState('descripcion');

  const loadStock = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '300' });
      if (search) params.set('q', search);
      if (soloStock) params.set('solo_stock', 'true');
      if (categoria) params.set('categoria', categoria);
      if (familiaFilter) params.set('familia', familiaFilter);
      const res = await api.get(`/bodega/stock?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Error cargando stock'); }
    finally { setLoading(false); }
  }, [search, soloStock, categoria, familiaFilter]);

  const loadClasificaciones = async () => {
    try {
      const res = await api.get('/bodega/clasificaciones');
      setClasificaciones(res.data);
    } catch { }
  };

  const loadConsumo = useCallback(async () => {
    setConsumoLoading(true);
    try {
      const params = new URLSearchParams();
      if (fechaDesde) params.set('fecha_desde', fechaDesde);
      if (fechaHasta) params.set('fecha_hasta', fechaHasta);
      const res = await api.get(`/bodega/consumo-por-familia?${params}`);
      setConsumo(res.data.consumos || []);
    } catch { toast.error('Error'); }
    finally { setConsumoLoading(false); }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { loadStock(); loadClasificaciones(); }, [loadStock]);

  // Carlos (10/05/2026): imprimir y exportar stock
  const imprimirStock = () => {
    if (!items.length) { toast.warning('No hay items para imprimir'); return; }
    printTable({
      title: 'Stock de Bodega',
      subtitle: `${items.length} items · ${categoria || 'Todas categorías'} · ${familiaFilter || 'Todas familias'} · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total items', value: total },
        { label: 'Con stock', value: items.filter(i => (i.stock || 0) > 0).length, color: '#047857' },
        { label: 'Categoría filtro', value: categoria || 'Todas' },
        { label: 'Familia filtro', value: familiaFilter || 'Todas' },
      ],
      columns: [
        { key: 'codigo', label: 'Código', mono: true },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'familia', label: 'Familia' },
        { key: 'unidad', label: 'Unidad', align: 'center' },
        { key: 'stock', label: 'Stock', align: 'right', mono: true, format: (v) => fmt(v) },
        { key: 'precio_unitario', label: 'Precio Unit.', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'valor_stock', label: 'Valor Stock', align: 'right', mono: true, format: (v, r) => `$${fmt((Number(r.stock) || 0) * (Number(r.precio_unitario) || 0))}` },
        { key: 'ubicacion', label: 'Ubicación' },
      ],
      rows: items,
    });
  };

  const exportarStockExcel = () => {
    if (!items.length) { toast.warning('No hay items para exportar'); return; }
    exportToCSV({
      filename: 'stock_bodega',
      title: 'CHIRINO STEEL · Stock de Bodega',
      summary: [
        { label: 'Total items', value: items.length },
        { label: 'Categoría', value: categoria || 'Todas' },
        { label: 'Familia', value: familiaFilter || 'Todas' },
        { label: 'Búsqueda', value: search || '-' },
        { label: 'Solo con precio', value: soloStock ? 'SÍ' : 'NO' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'codigo', label: 'Código' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'familia', label: 'Familia' },
        { key: 'unidad', label: 'Unidad' },
        { key: 'stock', label: 'Stock', format: (v) => Number(v) || 0 },
        { key: 'precio_unitario', label: 'Precio Unit. (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'valor_stock', label: 'Valor Stock (CLP)', format: (v, r) => Math.round((Number(r.stock) || 0) * (Number(r.precio_unitario) || 0)) },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: items,
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === 'consumo') { setDrilldown(null); loadConsumo(); } }, [activeTab, fechaDesde, fechaHasta]);

  const handleDrilldown = async (item, groupBy) => {
    const grp = groupBy || drillGroup;
    setDrilldown(item);
    setDrillGroup(grp);
    setDrillLoading(true);
    setOtSearch('');
    try {
      const params = new URLSearchParams({ agrupar: grp });
      if (fechaDesde) params.set('fecha_desde', fechaDesde);
      if (fechaHasta) params.set('fecha_hasta', fechaHasta);
      const res = await api.get(`/bodega/consumo-detalle/${item.codigo}?${params}`);
      setDrillData(res.data.detalle || []);
    } catch { toast.error('Error cargando detalle'); }
    finally { setDrillLoading(false); }
  };

  const handleOtSearch = async () => {
    if (!drilldown) return;
    setDrillLoading(true);
    try {
      const params = new URLSearchParams({ agrupar: 'ot' });
      if (fechaDesde) params.set('fecha_desde', fechaDesde);
      if (fechaHasta) params.set('fecha_hasta', fechaHasta);
      if (otSearch) params.set('buscar_ot', otSearch);
      const res = await api.get(`/bodega/consumo-detalle/${drilldown.codigo}?${params}`);
      setDrillData(res.data.detalle || []);
    } catch { toast.error('Error'); }
    finally { setDrillLoading(false); }
  };

  const [otSearch, setOtSearch] = useState('');

  const stats = clasificaciones?.stats || {};
  const subtipos = clasificaciones?.subtipos || [];

  return (
    <div className="space-y-5" data-testid="stock-bodega-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Boxes className="w-6 h-6 text-emerald-600" /> Inventario y Stock
        </h1>
        <p className="text-sm text-muted-foreground">Control de existencias, catálogo y consumos.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { id: 'stock', label: 'Stock Actual', icon: Package },
          { id: 'catalogo', label: 'Catálogo', icon: Filter },
          { id: 'consumo', label: 'Consumo por Familia', icon: BarChart3 },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === t.id ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-600'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      {clasificaciones && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border rounded p-3 text-center">
            <p className="text-xs text-muted-foreground">Materiales</p>
            <p className="text-xl font-bold">{stats.total_materiales?.toLocaleString()}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center">
            <p className="text-xs text-emerald-700">Ítems en Stock</p>
            <p className="text-xl font-bold text-emerald-700">{stats.total_stock?.toLocaleString()}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
            <p className="text-xs text-blue-700">Con Control Stock</p>
            <p className="text-xl font-bold text-blue-700">{stats.con_control_stock}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-center">
            <p className="text-xs text-amber-700">Herramientas</p>
            <p className="text-xl font-bold text-amber-700">{stats.herramientas}</p>
          </div>
        </div>
      )}

      {/* Stock Tab */}
      {activeTab === 'stock' && (
        <>
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px] max-w-sm">
                <label className="block text-xs font-medium mb-1">Buscar</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Descripción o código..." value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadStock()} className="pl-9" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Categoría</label>
                <div className="flex gap-1">
                  {[
                    { val: '', label: 'Todos', icon: null },
                    { val: 'herramienta', label: 'Herramientas', icon: Wrench },
                    { val: 'insumo', label: 'Insumos', icon: ShoppingBag },
                  ].map(c => (
                    <button key={c.val} onClick={() => setCategoria(c.val)}
                      className={`px-3 py-1.5 text-xs rounded-md border flex items-center gap-1 transition-colors ${categoria === c.val ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                      {c.icon && <c.icon className="w-3 h-3" />} {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Familia/Subtipo</label>
                <select value={familiaFilter} onChange={(e) => setFamiliaFilter(e.target.value)}
                  className="h-9 px-3 rounded-md border border-input text-xs min-w-[150px]">
                  <option value="">Todas</option>
                  {subtipos.map((s, i) => (
                    <option key={i} value={s.id_tipo}>{s.nombre}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={soloStock} onChange={(e) => setSoloStock(e.target.checked)} className="rounded" />
                Solo con precio
              </label>
              <Button variant="outline" onClick={loadStock} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="outline" size="sm" onClick={imprimirStock} title="Imprimir listado de stock" data-testid="btn-print-stock">
                <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
              </Button>
              <Button variant="outline" size="sm" onClick={exportarStockExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-stock">
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
              </Button>
              <span className="text-xs text-gray-500">{total} ítems</span>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
          ) : (
            <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
              <table className="w-full text-xs">
                <thead className="sticky top-14 lg:top-0 z-20">
                  <tr className="bg-gray-800 text-white">
                    <th className="px-3 py-2">Código</th>
                    <th className="px-3 py-2">Descripción</th>
                    <th className="px-3 py-2 text-center">Unidad</th>
                    <th className="px-3 py-2">Marca</th>
                    <th className="px-3 py-2 text-right">Precio</th>
                    <th className="px-3 py-2 text-right">Últ. Compra</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-1.5 font-mono text-[10px] text-gray-500">{item.codigo}</td>
                      <td className="px-3 py-1.5">{item.descripcion}</td>
                      <td className="px-3 py-1.5 text-center text-gray-500">{item.unidad}</td>
                      <td className="px-3 py-1.5 text-gray-500">{item.marca || '-'}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{item.precio ? `$${fmt(item.precio)}` : '-'}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{item.valor_ult_compra ? `$${fmt(item.valor_ult_compra)}` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {items.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">No se encontraron ítems</div>}
            </div>
          )}
        </>
      )}

      {/* Catálogo Tab */}
      {activeTab === 'catalogo' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold text-sm mb-3">Familias de Insumos ({clasificaciones?.familias_insumos?.length})</h3>
              <div className="space-y-1 max-h-[300px] overflow-y-auto">
                {(clasificaciones?.familias_insumos || []).map((f, i) => (
                  <div key={i} className="flex justify-between items-center bg-gray-50 rounded px-3 py-1.5 text-xs">
                    <span className="font-medium">{f.nombre}</span>
                    <span className="text-gray-400">{f.codigo} | {f.tipo}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold text-sm mb-3">Familias de Herramientas ({clasificaciones?.familias_herramientas?.length})</h3>
              <div className="space-y-1">
                {(clasificaciones?.familias_herramientas || []).map((f, i) => (
                  <div key={i} className="flex justify-between items-center bg-amber-50 rounded px-3 py-1.5 text-xs">
                    <span className="font-medium">{f.nombre}</span>
                    <span className="text-gray-400">{f.codigo}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-semibold text-sm mb-3">Grupos de Materiales ({clasificaciones?.grupos?.length})</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1 max-h-[300px] overflow-y-auto">
              {(clasificaciones?.grupos || []).map((g, i) => (
                <div key={i} className="bg-blue-50 rounded px-2 py-1 text-xs text-blue-800">{g.nombre}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Consumo Tab */}
      {activeTab === 'consumo' && (
        <>
          <div className="bg-white border rounded-lg p-4 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-medium mb-1">Desde</label>
              <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="w-40" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Hasta</label>
              <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="w-40" />
            </div>
            <Button onClick={loadConsumo} disabled={consumoLoading} className="bg-emerald-600 hover:bg-emerald-700">
              {consumoLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-1" />}
              Consultar
            </Button>
            <div className="flex gap-1">
              {[
                { label: 'Mes actual', fn: () => { setFechaDesde(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`); setFechaHasta(now.toISOString().split('T')[0]); }},
                { label: 'Último trimestre', fn: () => { const d = new Date(); d.setMonth(d.getMonth()-3); setFechaDesde(d.toISOString().split('T')[0]); setFechaHasta(now.toISOString().split('T')[0]); }},
                { label: 'Año actual', fn: () => { setFechaDesde(`${now.getFullYear()}-01-01`); setFechaHasta(now.toISOString().split('T')[0]); }},
                { label: 'Todo', fn: () => { setFechaDesde(''); setFechaHasta(''); }},
              ].map((p, i) => (
                <button key={i} onClick={() => { p.fn(); setDrilldown(null); }} className="px-2 py-1 text-[10px] rounded border border-gray-300 hover:bg-gray-50">{p.label}</button>
              ))}
            </div>
          </div>

          {consumoLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : drilldown ? (
            /* Drill-down view */
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <button onClick={() => { setDrilldown(null); setDrillData([]); }}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
                  <ChevronLeft className="w-4 h-4" /> Volver
                </button>
                <h3 className="font-semibold text-sm">{drilldown.nombre} ({drilldown.codigo})</h3>
              </div>
              <div className="flex gap-1">
                {[
                  { val: 'subfamilia', label: 'Por Subfamilia' },
                  { val: 'proveedor', label: 'Por Proveedor' },
                  { val: 'ot', label: 'Por OT' },
                ].map(g => (
                  <button key={g.val} onClick={() => handleDrilldown(drilldown, g.val)}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${drillGroup === g.val ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                    {g.label}
                  </button>
                ))}
                {drillGroup === 'ot' && (
                  <div className="flex gap-1 ml-2">
                    <Input placeholder="Buscar OT..." value={otSearch}
                      onChange={(e) => setOtSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleOtSearch()}
                      className="h-7 w-32 text-xs" />
                    <Button variant="outline" size="sm" className="h-7" onClick={handleOtSearch}>
                      <Search className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
              {drillLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : (
                <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
                  <table className="w-full text-xs">
                    <thead className="sticky top-14 lg:top-0 z-20">
                      <tr className="bg-emerald-700 text-white">
                        <th className="px-3 py-2 text-left">{drillGroup === 'proveedor' ? 'Proveedor' : drillGroup === 'ot' ? 'OT' : 'Subfamilia'}</th>
                        {drillGroup === 'proveedor' && <th className="px-3 py-2">OC</th>}
                        <th className="px-3 py-2 text-right">Ítems</th>
                        <th className="px-3 py-2 text-right">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillData.map((d, i) => (
                        <tr key={i} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{d.nombre}</td>
                          {drillGroup === 'proveedor' && <td className="px-3 py-2 font-mono text-gray-500">{d.numero_oc}</td>}
                          <td className="px-3 py-2 text-right">{d.items}</td>
                          <td className="px-3 py-2 text-right font-mono font-bold">${fmt(d.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t font-bold">
                        <td colSpan={drillGroup === 'proveedor' ? 2 : 1} className="px-3 py-2 text-right">TOTAL:</td>
                        <td className="px-3 py-2 text-right">{drillData.reduce((s, d) => s + d.items, 0)}</td>
                        <td className="px-3 py-2 text-right font-mono">${fmt(drillData.reduce((s, d) => s + d.total, 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
              <table className="w-full text-xs">
                <thead className="sticky top-14 lg:top-0 z-20">
                  <tr className="bg-gray-800 text-white">
                    <th className="px-3 py-2">Cuenta</th>
                    <th className="px-3 py-2">Descripción</th>
                    <th className="px-3 py-2 text-right">Ítems</th>
                    <th className="px-3 py-2 text-right">Monto Total</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {consumo.map((c, i) => (
                    <tr key={i} className="border-t hover:bg-emerald-50 cursor-pointer" onClick={() => handleDrilldown(c, 'descripcion')}>
                      <td className="px-3 py-2 font-mono text-gray-600">{c.codigo}</td>
                      <td className="px-3 py-2 font-medium">{c.nombre}</td>
                      <td className="px-3 py-2 text-right">{c.total_items?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold">${fmt(c.total_monto)}</td>
                      <td className="px-2 py-2 text-emerald-600"><ChevronRight className="w-4 h-4" /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 border-t font-bold">
                    <td colSpan={2} className="px-3 py-2 text-right">TOTAL:</td>
                    <td className="px-3 py-2 text-right">{consumo.reduce((s, c) => s + c.total_items, 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono">${fmt(consumo.reduce((s, c) => s + c.total_monto, 0))}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
              {consumo.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">No hay datos para el período seleccionado</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
